import { IoCheckmarkDoneSharp } from "react-icons/io5";
import { IoCheckmarkDone } from "react-icons/io5";
import { RiSendPlaneFill } from "react-icons/ri";
import { BiCamera } from "react-icons/bi";
import { RiAttachment2 } from "react-icons/ri";
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { scheduleMessage, getScheduled, subscribe, cancelScheduled } from '../../utils/messageScheduler';
import { useSelector } from 'react-redux';
import { compressImages } from '../../utils/imageUtils';
import MessageImages from '../../components/MessageImages';

const API_URL = process.env.REACT_APP_API_URL;

const ReceivedEmails = () => {
  const [receivedMessages, setReceivedMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [message, setMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [uniqueUsers, setUniqueUsers] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [fullConversationHistory, setFullConversationHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingFullHistory, setLoadingFullHistory] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  // Scheduling state
  const [enableSchedule, setEnableSchedule] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [scheduledItems, setScheduledItems] = useState([]);
  // Image upload state
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [isCompressingImages, setIsCompressingImages] = useState(false);

  useEffect(() => {
    // Fetch current user ID
    const fetchCurrentUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCurrentUserId(response.data._id);
      } catch (error) {
        console.error('Error fetching current user:', error);
      }
    };
    fetchCurrentUser();

    // Prefill from Redux cache for faster initial paint
    if (cachedAdminSentMessages.length > 0) {
      // For user: received messages are those sent FROM admin to user and already read
      const readFromAdmin = cachedAdminSentMessages.filter(m => m?.is_read);
      setReceivedMessages(readFromAdmin);
      setLoading(false);
    }
    fetchReceivedMessages();

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Pull admin-sent from Redux cache (messages sent FROM admin)
  const cachedPages = useSelector(state => state.messages?.pagesByKey || {});
  const cachedAdminSentMessages = useMemo(() => {
    try {
      const all = Object.values(cachedPages).map(p => p?.messages || []).flat();
      return all.filter(m => m?.from_userID?.role === 'admin');
    } catch (_) { return []; }
  }, [cachedPages]);

  useEffect(() => {
    const unsubscribe = subscribe((items) => {
      setScheduledItems(items.filter((it) => it.source === 'user:received'));
    });
    setScheduledItems(getScheduled().filter((it) => it.source === 'user:received'));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Extract unique users from messages
    const users = [...new Set(receivedMessages.map(msg => msg.from_userID?._id))].filter(Boolean);
    const userDetails = users.map(userId => {
      const message = receivedMessages.find(msg => msg.from_userID?._id === userId);
      return {
        id: userId,
        name: message?.from_userID?.name || message?.from_userID?.userID || '不明',
        email: message?.from_userID?.email || ''
      };
    });
    setUniqueUsers(userDetails);

    // Filter messages based on selected user
    if (selectedUser === '') {
      setFilteredMessages(receivedMessages);
    } else {
      const filtered = receivedMessages.filter(msg => msg.from_userID?._id === selectedUser);
      setFilteredMessages(filtered);
    }
  }, [receivedMessages, selectedUser]);

  const fetchReceivedMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/messages/inbox`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to show only read messages
      const readMessages = response.data.filter(msg => msg.is_read);
      setReceivedMessages(readMessages);
    } catch (error) {
      console.error('Error fetching received messages:', error);
      alert('受信済みメッセージの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };


  // Emoji picker data
  const emojis = [
    '😊', '😄', '😍', '🥰', '😘', '😉', '😋', '😎', '🤗', '🤩',
    '👍', '👎', '👌', '✌️', '🤞', '👏', '🙌', '🤝', '💪', '👊',
    '❤️', '💖', '💕', '💗', '💓', '💝', '💘', '💞', '💟', '💌',
    '🌹', '🌸', '🌺', '🌷', '🌼', '🌻', '💐', '🌱', '🌿', '🍀',
    '🎉', '🎊', '🎈', '🎂', '🎁', '🎀', '🎪', '🎭', '🎨', '🎬',
    '☀️', '🌙', '⭐', '🌟', '✨', '💫', '🌈', '☁️', '🌤️', '🌅'
  ];

  const handleReply = async (msg) => {
    setSelectedMessage(msg);
    setShowMessageModal(true);
    setMessage('');
    setEnableSchedule(false);
    setScheduledAt('');
    
    // Fetch conversation history - first get only 1 recent message for quick display
    if (msg.from_userID?._id) {
      setLoadingHistory(true);
      try {
        const token = localStorage.getItem('token');
        // First: Get only 1 most recent message for quick display
        const response = await axios.get(`${API_URL}/api/messages/conversation/${msg.from_userID._id}?limit=1`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setConversationHistory(response.data);
        setLoadingHistory(false);
        
        // Then: Fetch full 200 messages in background
        setLoadingFullHistory(true);
        const fullResponse = await axios.get(`${API_URL}/api/messages/conversation/${msg.from_userID._id}?limit=200`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFullConversationHistory(fullResponse.data);
      } catch (error) {
        console.error('Error fetching conversation history:', error);
        setConversationHistory([]);
        setFullConversationHistory([]);
      } finally {
        setLoadingHistory(false);
        setLoadingFullHistory(false);
      }
    }
  };

  const openFullHistoryInNewTab = () => {
    if (!selectedMessage?.from_userID) return;
    
    // Use pre-fetched full history (200 messages)
    const history = fullConversationHistory.length > 0 ? fullConversationHistory : conversationHistory;
    const doc = window.open('', '_blank');
    if (!doc) return;
    
    const title = `${selectedMessage.from_userID.name || '不明'}とのやり取り履歴`;
    const html = `<!doctype html>
<html lang="ja">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${title}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans JP', 'Hiragino Kaku Gothic ProN', Meiryo, sans-serif; margin: 20px; padding-bottom: 80px; background: #f5f5f5; }
    h2 { margin: 0 0 16px; color: #333; }
    .msg { border: 1px solid #ddd; border-radius: 8px; padding: 10px 12px; margin: 8px 0; background: white; cursor: pointer; transition: box-shadow 0.2s; }
    .msg:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
    .meta { color: #666; font-size: 12px; margin-bottom: 6px; }
    .from { font-weight: bold; color: #34495e; }
    .body { word-wrap: break-word; overflow-wrap: break-word; line-height: 1.6; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
    .from-me { background-color: #e3f2fd; border-left: 3px solid #2196f3; }
    .from-other { background-color: #f5f5f5; border-left: 3px solid #4caf50; }
    .back-button { position: fixed; bottom: 20px; right: 20px; background-color: #2196f3; color: white; border: none; padding: 12px 24px; border-radius: 5px; cursor: pointer; font-size: 14px; box-shadow: 0 2px 8px rgba(0,0,0,0.2); }
    .back-button:hover { background-color: #1976d2; }
    .modal { display: none; position: fixed; z-index: 1000; left: 0; top: 0; width: 100%; height: 100%; background-color: rgba(0,0,0,0.5); }
    .modal-content { background-color: white; margin: 5% auto; padding: 20px; border-radius: 10px; width: 80%; max-width: 600px; max-height: 70vh; overflow-y: auto; }
    .close-modal { color: #999; float: right; font-size: 28px; font-weight: bold; cursor: pointer; }
    .close-modal:hover { color: #333; }
    .modal-body { white-space: pre-wrap; word-wrap: break-word; line-height: 1.6; margin-top: 10px; }
  </style>
  <script>
    function openModal(content, sender, date) {
      const modal = document.getElementById('messageModal');
      document.getElementById('modalSender').textContent = sender;
      document.getElementById('modalDate').textContent = date;
      document.getElementById('modalBody').textContent = content;
      modal.style.display = 'block';
    }
    function closeModal() {
      document.getElementById('messageModal').style.display = 'none';
    }
    window.onclick = function(event) {
      const modal = document.getElementById('messageModal');
      if (event.target == modal) {
        closeModal();
      }
    }
  </script>
</head>
<body>
  <h2>${title} (直近200件)</h2>
  ${history.map(h => {
    const isFromMe = h.from_userID?._id === currentUserId;
    const escapedBody = (h.body || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g,'&gt;').replace(/'/g, '&apos;').replace(/"/g, '&quot;');
    const sender = (h.from_userID?.name || '不明').replace(/'/g, '&apos;');
    const date = new Date(h.created_at).toLocaleString('ja-JP');
    return `
    <div class="msg ${isFromMe ? 'from-me' : 'from-other'}" onclick="openModal('${escapedBody}', '${sender}', '${date}')">
      <div class="meta">
        <span class="from">${h.from_userID?.name || '不明'}</span>
        ／ ${date}
      </div>
      <div class="body">${escapedBody}</div>
    </div>
  `;
  }).join('')}
  <div id="messageModal" class="modal">
    <div class="modal-content">
      <span class="close-modal" onclick="closeModal()">&times;</span>
      <h3 id="modalSender"></h3>
      <p style="color: #666; font-size: 12px;" id="modalDate"></p>
      <div class="modal-body" id="modalBody"></div>
    </div>
  </div>
  <button class="back-button" onclick="window.close()">戻る</button>
</body>
</html>`;
    doc.document.open();
    doc.document.write(html);
    doc.document.close();
  };

  const handleViewDetail = (msg) => {
    setSelectedMessage(msg);
    setShowDetailModal(true);
  };

  const addEmoji = (emoji) => {
    setMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/') && !file.type.startsWith('video/'));
    
    if (imageFiles.length === 0) {
      alert('画像ファイルを選択してください（動画ファイルは除く）');
      return;
    }

    if (selectedImages.length + imageFiles.length > 10) {
      alert('画像は最大10枚まで選択できます');
      return;
    }

    setIsCompressingImages(true);
    try {
      const compressedImages = await compressImages(imageFiles);
      setSelectedImages(prev => [...prev, ...compressedImages]);
      setImagePreviewUrls(prev => [...prev, ...compressedImages]);
      // NOTE: Do not inject placeholder text into textarea; images are shown via previews/thumbnails.
    } catch (error) {
      console.error('Error compressing images:', error);
      alert('画像の圧縮に失敗しました');
    } finally {
      setIsCompressingImages(false);
    }
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const sendReply = async () => {
    if (!message.trim() && selectedImages.length === 0) {
      alert('メッセージまたは画像を入力してください');
      return;
    }

    // Determine if recipient is Admin (skip point deduction when sending to Admin)
    const isRecipientAdmin = selectedMessage?.from_userID?.role === 'admin';

    // If scheduled in the future, queue instead
    if (enableSchedule && scheduledAt) {
      const when = new Date(scheduledAt);
      if (when.getTime() > Date.now()) {
        scheduleMessage({
          recipients: [selectedMessage.from_userID._id],
          content: message,
          images: selectedImages,
          scheduledAt: when.toISOString(),
          source: 'user:received',
          type: 'reply',
          relatedMessageId: selectedMessage._id,
          markAsReplied: true,
          // Deduct points at dispatch time unless recipient is Admin
          needsPointDeduction: !isRecipientAdmin
        });
        setShowMessageModal(false);
        setMessage('');
        setSelectedImages([]);
        setImagePreviewUrls([]);
        setSelectedMessage(null);
        return;
      }
    }

    try {
      const token = localStorage.getItem('token');

      // If recipient is not Admin, deduct points before sending (no confirmation UI)
      if (!isRecipientAdmin) {
        try {
          const deductResponse = await axios.post(`${API_URL}/api/users/deduct-points`, {
            actionName: 'send_message'
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (!deductResponse.data?.success) {
            alert(`ポイントが不足しています。\n必要ポイント: ${deductResponse.data?.required ?? ''}pt\n所持ポイント: ${deductResponse.data?.available ?? ''}pt`);
            return;
          }

          // Notify global listener to update points in sidebar
          try {
            window.dispatchEvent(new CustomEvent('points:deducted', { detail: { remainingPoints: deductResponse.data.remainingPoints } }));
          } catch (_) { }
        } catch (err) {
          console.error('Error during point deduction:', err);
          alert(err?.response?.data?.message || 'ポイントの確認/減算に失敗しました');
          return;
        }
      }

      // Send the reply message
      await axios.post(`${API_URL}/api/messages/recipientId`, {
        userId: selectedMessage.from_userID._id,
        content: message,
        images: selectedImages
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Mark the original message as having a reply
      await axios.put(`${API_URL}/api/messages/${selectedMessage._id}/reply`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh the messages to show updated has_reply status
      fetchReceivedMessages();

      setShowMessageModal(false);
      setMessage('');
      setSelectedImages([]);
      setImagePreviewUrls([]);
      setSelectedMessage(null);
      setConversationHistory([]);
    } catch (error) {
      console.error('Error sending reply:', error);
      alert(error.response?.data?.message || '返信の送信に失敗しました');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p>読み込み中...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '10px' : '0px' }}>
      <h2 style={{
        color: '#333',
        marginBottom: '20px',
        fontSize: isMobile ? '20px' : '24px'
      }}>受信済みメッセージ</h2>

      {/* Scheduled Items Panel */}
      {scheduledItems.length > 0 && (
        <div style={{
          marginBottom: '15px',
          backgroundColor: 'white',
          border: '1px dashed #f39c12',
          padding: '10px',
          borderRadius: '6px'
        }}>
          <div style={{ fontWeight: 'bold', color: '#f39c12', marginBottom: '8px' }}>⏳ 予約中のメッセージ</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {scheduledItems.map((it) => (
              <div key={it.id} style={{
                border: '1px solid #f8c471',
                borderRadius: '4px',
                padding: '6px 8px',
                background: it.status === 'failed' ? '#fdecea' : '#fcf3cf'
              }}>
                <div style={{ fontSize: '12px' }}>
                  <span style={{ marginRight: '6px' }}>🕒 {new Date(it.scheduledAt).toLocaleString('ja-JP')}</span>
                  <span style={{ color: '#7f8c8d' }}>{it.type}</span><span style={{ marginLeft: '8px', color: '#34495e' }}>宛先: {(Array.isArray(it.recipients) ? (receivedMessages.find(m => m.from_userID?._id === it.recipients[0])?.from_userID?.name) : (receivedMessages.find(m => m.from_userID?._id === it.recipients)?.from_userID?.name)) || '不明'}</span>
                  {it.status === 'failed' && (
                    <span style={{ color: '#e74c3c', marginLeft: '6px' }}>失敗: {it.lastError}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                  <button
                    onClick={() => { if (window.confirm('この予約を取り消しますか？')) { cancelScheduled(it.id); } }}
                    style={{
                      background: '#e74c3c',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    取り消し
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User Search Dropdown */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            style={{
              padding: isMobile ? '10px' : '12px',
              border: '1px solid #ddd',
              borderRadius: '5px',
              fontSize: isMobile ? '14px' : '16px',
              minWidth: isMobile ? '250px' : '300px',
              backgroundColor: 'white'
            }}
          >
            <option value="">すべてのユーザー</option>
            {uniqueUsers.map((user) => (
              <option key={user.id} value={user.id}>
                {/* {user.name} {user.email ? `(${user.email})` : ''} */}
                {user.name}
              </option>
            ))}
          </select>
          <div style={{ color: '#666', fontSize: isMobile ? '12px' : '14px' }}>
            {selectedUser === '' ?
              `全${receivedMessages.length}件` :
              `選択されたユーザー: ${filteredMessages.length}件`
            }
          </div>
        </div>
      </div>

      {receivedMessages.length === 0 ? (
        <div style={{
          backgroundColor: 'white',
          padding: isMobile ? '20px' : '40px',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <p style={{ fontSize: isMobile ? '16px' : '18px', color: '#666' }}>受信済みメッセージはありません</p>
          <p style={{ color: '#999', marginTop: '10px', fontSize: isMobile ? '14px' : '16px' }}>未読メッセージを既読にすると、ここに表示されます</p>
        </div>
      ) : (
        <div style={{
          backgroundColor: 'white',
          padding: isMobile ? '15px' : '20px',
          borderRadius: '8px'
        }}>
          <h3 style={{
            marginBottom: '20px',
            fontSize: isMobile ? '16px' : '18px'
          }}>受信済みメッセージ ({receivedMessages.length}件)</h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {filteredMessages.map((msg) => (
              <div
                key={msg._id}
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  padding: isMobile ? '15px' : '20px',
                  backgroundColor: '#fafafa'
                }}
              >
                {/* Sender Info */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? '10px' : '15px',
                  marginBottom: isMobile ? '8px' : '15px',
                  flexWrap: isMobile ? 'wrap' : 'nowrap'
                }}>
                  {msg.from_userID?.photoLink ? (
                    <img
                      src={msg.from_userID.photoLink.photodatas || msg.from_userID.photoLink}
                      alt="Profile"
                      style={{
                        width: isMobile ? '35px' : '40px',
                        height: isMobile ? '35px' : '40px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        flexShrink: 0
                      }}
                    />
                  ) : (
                    <div style={{
                      width: isMobile ? '35px' : '40px',
                      height: isMobile ? '35px' : '40px',
                      borderRadius: '50%',
                      backgroundColor: '#ddd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: isMobile ? '10px' : '12px',
                      flexShrink: 0
                    }}>
                      なし
                    </div>
                  )}
                  
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontWeight: 'bold',
                      fontSize: isMobile ? '14px' : '16px'
                    }}>
                      {msg.from_userID?.name || 'Unknown'}
                    </div>
                  </div>
                  
                </div>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    flexShrink: 0
                  }}>
                    <button
                      onClick={() => handleReply(msg)}
                      style={{
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        padding: isMobile ? '6px 12px' : '8px 16px',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: isMobile ? '11px' : '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <RiSendPlaneFill />
                      {isMobile ? '' : '返信'}
                    </button>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      color: '#28a745',
                      fontSize: isMobile ? '14px' : '16px'
                    }}>
                      <IoCheckmarkDoneSharp />
                      {/* <span style={{ fontSize: isMobile ? '12px' : '14px' }}>既読済み</span> */}
                      {isMobile ? '' : '既読済み'}
                    </div>
                  </div>
                {/* Message Content */}
                <div 
                  style={{ marginBottom: '15px', cursor: 'pointer' }}
                  onClick={() => handleViewDetail(msg)}
                >
                  <div style={{
                    lineHeight: '1.5',
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    fontSize: isMobile ? '14px' : '16px',
                    maxHeight: '4.5em',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    {msg.body}
                    {msg.body && msg.body.length > 100 && (
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        background: 'linear-gradient(transparent, #fafafa)',
                        width: '50px',
                        height: '1.5em'
                      }}></div>
                    )}
                  </div>
                  {msg.images && msg.images.length > 0 && (
                    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: '10px' }}>
                      <MessageImages images={msg.images} style={{ maxHeight: '50px' }}/>
                    </div>
                  )}
                  {/* {msg.body && msg.body.length > 100 && (
                    <div style={{
                      color: '#007bff',
                      fontSize: isMobile ? '12px' : '14px',
                      marginTop: '8px'
                    }}>
                      クリックして詳細を表示
                    </div>
                  )} */}
                </div>

                {/* Timestamp */}
                <div style={{
                  color: '#666',
                  fontSize: isMobile ? '12px' : '14px',
                  borderTop: '1px solid #eee',
                  paddingTop: '10px'
                }}>
                  受信日時: {new Date(msg.created_at).toLocaleString('ja-JP')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reply Message Modal */}
      {showMessageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          padding: '5px',
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '5px',
            width: '500px',
            // maxWidth: '90%'
          }}>
            <h3 style={{ marginBottom: '10px' }}>
              {selectedMessage?.from_userID?.name}さんに返信
            </h3>

            {/* Conversation History */}
            {conversationHistory.length > 0 && (
              <div style={{
                marginBottom: '5px',
                maxHeight: '100px',
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: '5px',
                padding: '5px',
                backgroundColor: '#f9f9f9'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#666' }}>
                    直前のやり取り
                  </div>
                  <button
                    onClick={openFullHistoryInNewTab}
                    style={{
                      backgroundColor: '#2196f3',
                      color: 'white',
                      border: 'none',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '11px'
                    }}
                  >
                    📋
                  </button>
                </div>
                {conversationHistory.slice(-1).map((msg) => {
                  // Check if message is from current user by comparing IDs
                  const isFromMe = msg.from_userID?._id === currentUserId;
                  return (
                    <div
                      key={msg._id}
                      style={{
                        marginBottom: '10px',
                        padding: '8px',
                        backgroundColor: isFromMe ? '#e3f2fd' : '#f5f5f5',
                        borderRadius: '5px',
                        borderLeft: `3px solid ${isFromMe ? '#2196f3' : '#4caf50'}`
                      }}
                    >
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                        {msg.from_userID?.name || '不明'} - {new Date(msg.created_at).toLocaleString('ja-JP')}
                      </div>
                      <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {(msg.body || '').replace(/\[画像添付\]\n?/g, '').trim()}
                      </div>
                      {msg.images && msg.images.length > 0 && (
                        <MessageImages images={msg.images} style={{ maxHeight: '50px' }} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {loadingHistory && (
              <div style={{ marginBottom: '15px', textAlign: 'center', color: '#666' }}>
                会話履歴を読み込み中...
              </div>
            )}

            <div style={{ marginBottom: '3px' }}>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="返信メッセージを入力してください..."
                style={{
                  width: '93%',
                  height: '100px',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  resize: 'vertical',
                  marginBottom: '10px'
                }}
              />
            </div>

            {/* Image Upload + Emoji controls (match ProfileSearch layout) */}
            <div style={{}}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <label
                    style={{
                      border: '1px solid #3498db',
                      color: 'black',
                      padding: '8px 10px 0px 10px ',
                      borderRadius: '5px',
                      cursor: (isCompressingImages || selectedImages.length >= 10) ? 'not-allowed' : 'pointer',
                      fontSize: '20px',
                      display: 'inline-block',
                      opacity: (isCompressingImages || selectedImages.length >= 10) ? 0.6 : 1
                    }}
                  >
                    <RiAttachment2 />
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageSelect}
                      disabled={isCompressingImages || selectedImages.length >= 10}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <label
                    style={{
                      border: '1px solid #27ae60',
                      color: 'black',
                      padding: '8px 10px 0px 10px ',
                      borderRadius: '5px',
                      cursor: (isCompressingImages || selectedImages.length >= 10) ? 'not-allowed' : 'pointer',
                      fontSize: '20px',
                      display: 'inline-block',
                      opacity: (isCompressingImages || selectedImages.length >= 10) ? 0.6 : 1
                    }}
                  >
                    <BiCamera />
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleImageSelect}
                      disabled={isCompressingImages || selectedImages.length >= 10}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
                <div>
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    style={{
                      backgroundColor: '#ff9800',
                      color: 'white',
                      border: 'none',
                      padding: '8px 8px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      marginRight: '10px'
                    }}
                  >
                    😊 絵文字
                  </button>
                </div>
              </div>
              {isCompressingImages && (
                <div style={{ color: '#666', fontSize: '12px', marginBottom: '10px' }}>
                  画像を圧縮中...
                </div>
              )}
              {imagePreviewUrls.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                  gap: '10px',
                  marginTop: '10px'
                }}>
                  {imagePreviewUrls.map((url, index) => (
                    <div key={index} style={{ position: 'relative' }}>
                      <img
                        src={url}
                        alt={`Preview ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '5px',
                          border: '1px solid #ddd'
                        }}
                      />
                      <button
                        onClick={() => removeImage(index)}
                        style={{
                          position: 'absolute',
                          top: '5px',
                          right: '5px',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Schedule controls */}
            <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              {/* <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input type="checkbox" checked={enableSchedule} onChange={(e) => setEnableSchedule(e.target.checked)} />
                後で送信を予約する
              </label> */}
              {enableSchedule && (
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              )}
            </div>

            {/* Emoji Picker */}
            {showEmojiPicker && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '10px',
                padding: '20px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                zIndex: 1001,
                maxWidth: '80%',
                maxHeight: '30%',
                overflow: 'auto'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }}>
                  {emojis.map((emoji, index) => (
                    <button
                      key={index}
                      onClick={() => addEmoji(emoji)}
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        fontSize: '20px',
                        cursor: 'pointer',
                        padding: '5px',
                        borderRadius: '5px',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowEmojiPicker(false)}
                  style={{
                    position: 'absolute',
                    top: '2px',
                    right: '5px',
                    backgroundColor: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    padding: '5px 10px',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => {
                  setShowMessageModal(false);
                  setConversationHistory([]);
                  setFullConversationHistory([]);
                }}
                style={{
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={sendReply}
                disabled={!message.trim() && selectedImages.length === 0}
                style={{
                  backgroundColor: (message.trim() || selectedImages.length > 0) ? '#27ae60' : '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: (message.trim() || selectedImages.length > 0) ? 'pointer' : 'not-allowed'
                }}
              >
                {enableSchedule && scheduledAt ? '予約' : '送信'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detailed View Modal */}
      {showDetailModal && selectedMessage && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '15px',
            borderRadius: '10px',
            width: '90%',
            maxWidth: '600px',
            height: '60vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '1px solid #eee',
              paddingBottom: '15px'
            }}>
              <h3 style={{ margin: 0, color: '#333' }}>受信メッセージ詳細</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                ✕
              </button>
            </div>
            
            {/* Sender Info */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '15px',
              marginBottom: '20px',
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px'
            }}>
              {selectedMessage.from_userID?.photoLink ? (
                <img
                  src={selectedMessage.from_userID.photoLink.photodatas || selectedMessage.from_userID.photoLink}
                  alt="Profile"
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: '#ddd',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px'
                }}>
                  なし
                </div>
              )}
              <div>
                <div style={{
                  fontWeight: 'bold',
                  fontSize: '18px',
                  marginBottom: '5px'
                }}>
                  送信者: {selectedMessage.from_userID?.name || 'Unknown'}
                </div>
                <div style={{
                  color: '#666',
                  fontSize: '14px'
                }}>
                  受信日時: {new Date(selectedMessage.created_at).toLocaleString('ja-JP')}
                </div>
              </div>
              <div style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                color: '#28a745'
              }}>
                {/* <IoCheckmarkDoneSharp />
                <span>既読済み</span> */}
              </div>
            </div>
            
            {/* Message Content */}
            <div style={{
              backgroundColor: '#fff',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid #eee',
              marginBottom: '20px',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#333', flexShrink: 0 }}>メッセージ内容</h4>
              <div style={{
                lineHeight: '1.6',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                fontSize: '16px',
                color: '#333',
                overflow: 'auto',
                flex: 1,
                marginBottom: selectedMessage.images && selectedMessage.images.length > 0 ? '15px' : '0'
              }}>
                {selectedMessage.body}
              </div>
              {selectedMessage.images && selectedMessage.images.length > 0 && (
                <div style={{ flexShrink: 0 }}>
                  <MessageImages images={selectedMessage.images} />
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            {/* <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              flexShrink: 0
            }}>
              <button
                onClick={() => setShowDetailModal(false)}
                style={{
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                閉じる
              </button>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  handleReply(selectedMessage);
                }}
                style={{
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                返信する
              </button>
            </div> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceivedEmails; 